<script setup lang="ts">
import { useElementSize } from "@vueuse/core"

const el = ref(null)
const grid = ref<(boolean | null)[][]>([])
const rows = ref(0)
const cols = ref(0)

const { width, height } = useElementSize(el)

function createGrid() {
	grid.value = []

	for (let i = 0; i <= rows.value; i++) {
		grid.value.push(Array.from<boolean | null>({ length: cols.value }).fill(null))
	}
}

function createNewCell() {
	const x = Math.floor(Math.random() * cols.value)

	grid.value[0]![x] = true
}

function moveCellsDown() {
	for (let row = rows.value - 1; row >= 0; row--) {
		for (let col = 0; col < cols.value; col++) {
			if (grid.value[row]![col] !== null && grid.value[row + 1]![col] === null) {
				grid.value[row + 1]![col]! = grid.value[row]![col]!
				grid.value[row]![col] = null
			}
		}
	}

	setTimeout(() => {
		if (grid.value[rows.value]!.every(cell => cell !== null)) {
			for (let col = 0; col < cols.value; col++) {
				grid.value[rows.value]![col] = null
			}
		}
	}, 500)
}

function removeCell(row: number, col: number) {
	grid.value[row]![col] = null
}

function calcGrid() {
	const base = Math.ceil(width.value / 60)
	const cell = width.value / base

	rows.value = Math.ceil(height.value / cell)
	cols.value = width.value / cell

	createGrid()
}

watch(width, calcGrid)

onMounted(() => {
	setTimeout(calcGrid, 50)

	setInterval(() => {
		moveCellsDown()
		createNewCell()
	}, 1000)
})
</script>

<template>
	<ClientOnly>
		<Transition
			appear
			name="fade">
			<div
				ref="el"
				:style="{
					'--cell': `${width / cols}px`,
					'--rows': rows - 1,
				}"
				class="absolute inset-0 grid auto-rows-[--cell] justify-center -space-y-px">
				<div
					v-for="(row, rowIndex) in grid"
					:key="rowIndex"
					class="grid auto-cols-[--cell] grid-flow-col flex-1 -space-x-px">
					<div
						v-for="(cell, cellIndex) in row"
						:key="cellIndex"
						class="border-primary-200/50 dark:border-primary-900/25 relative border">
						<div
							class="will-change-[opacity] absolute inset-0 bg-primary/10 transition-opacity duration-1000 dark:bg-primary/5 hover:bg-primary/20 dark:hover:bg-primary/10"
							:class="[cell ? 'opacity-100 cursor-pointer' : 'opacity-0']"
							@click="cell && removeCell(rowIndex, cellIndex)" />
					</div>
				</div>

				<div
					:style="{
						'--un-gradient-shape': 'to top',
					}"
					class="pointer-events-none absolute inset-x-0 top-[calc((var(--cell)*var(--rows))+1px)] h-[calc(var(--cell)*2)] from-background bg-gradient-to-t dark:from-gray-900" />
			</div>
		</Transition>

		<template #fallback>
			<NuxtImg
				src="/website/pattern.png"
				format="webp"
				quality="1"
				:preload="{ fetchPriority: 'high' }"
				class="absolute inset-0 h-[calc(100vh+64px)]" />
		</template>
	</ClientOnly>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
